import moment from 'moment';
import cookies from 'js-cookie';
import axios from 'axios';
import Printer from './qz-print';
import { GET_COMPANY_LOGO } from './api';
import { showNotification, updateNotification } from './Notification/Notification';
import { NotificationData } from '@mantine/notifications';
import { formatMoneyString, getCustomerFullName } from './strings';
import { toPng } from 'html-to-image';
import download from 'downloadjs';
import { utils as wdUtils } from 'washday-sdk';
import QRCode from 'qrcode';
import { t as translate } from 'i18next';
// @ts-ignore jsbarcode ships without types in this project
import JsBarcode from 'jsbarcode';
import {
  buildQrPayload,
  resolveOrderDisplayTotals,
  getOrderAmountDue,
  resolveFinancialDataMode,
  resolveEffectiveOrderLineCharge,
  resolveLabelSettings,
  sendLabelBatch,
  type LabelRenderOptions,
  type LabelSettingsInput,
  type ServiceLabelLine,
} from '@washday/utilities';
import { buildOrderFolio, normalizeOrderScheduleTimeValue } from './ordersUtils';
import { ITicketForLaundryStructure, ITicketStructure, LabelCodeSize } from '../interfaces/Store';
import RegimenFiscalEnum, { RegimenFiscalType } from '../enum/RegimenFiscalEnum';
import ProductType from '../enum/ProductType';
import { escapeLabelHtml, getLabelPreset, getLabelPrefs, openLabelInNewTab, LabelSize } from './labelHelper';
import { getPiecesBreakdown, formatPiecesBreakdown, getMeasuredUnitLabel } from './helpers/getAllPieces';
import { fitLabelHtmlForPrint, normalizeBase64Image, renderHtmlToPngBase64 } from './labelRender';
import { pngBase64ToPdfBase64 } from './labelPdf';
import { buildPublicAppUrl } from './publicAppUrl';
import {
  createEscPosQzPrintData,
  generateEscPosTicketForFrontend,
  resolveEscPosPaperWidth,
  shouldUseEscPosTicketFormat,
} from './ticketEscPos';


function getFontSize(fontSizeOption: "small" | "medium" | "large") {
  switch (fontSizeOption) {
    case "small":
      return 16;
    case "medium":
      return 24;
    case "large":
      return 32;
    default:
      return 16;
  }
}

let isPrintingLabel = false;

export type { LabelRenderOptions } from '@washday/utilities';
type LabelTranslator = (key: string, options?: Record<string, unknown>) => unknown;
type WebLabelRenderOptions = LabelRenderOptions & { t?: LabelTranslator };
type LabelOrderInput = {
  _id?: string;
  folioPrefix?: string | null;
  sequence?: string | number | null;
  customer?: unknown;
  customerInfo?: unknown;
  createdDate?: Date | string | null;
  deliveryInfo?: unknown;
  products?: readonly unknown[];
  buyAndGetProducts?: readonly unknown[];
  total?: number;
  paymentLines?: unknown;
};
type LabelStoreInput = {
  name?: string;
  currencySymbol?: string;
  measurementUnits?: unknown;
  labelTicketStructure?: LabelSettingsInput | null;
} | string | null | undefined;

function formatTicketScheduleTime(value?: string) {
  return normalizeOrderScheduleTimeValue(value);
}

function buildPickupInfoLabel(pickupInfo?: { date?: string; fromTime?: string; toTime?: string }) {
  if (!pickupInfo?.date) return '';

  const pickupDate = moment(pickupInfo.date);
  if (!pickupDate.isValid()) return '';

  const fromTime = formatTicketScheduleTime(pickupInfo.fromTime);
  const toTime = formatTicketScheduleTime(pickupInfo.toTime);
  const schedule = fromTime && toTime ? `${fromTime} - ${toTime}` : fromTime;

  return [pickupDate.format('DD/MM/YYYY'), schedule].filter(Boolean).join(' ');
}

type LabelPrintMode = 'pdf' | 'html';

const COMPANY_LOGO_BASE64_KEY = 'companyLogoBase64';
const COMPANY_LOGO_URL_KEY = 'companyLogoUrl';
const DEFAULT_TICKET_LOGO_URL = buildPublicAppUrl('/assets/logo/washday_logo.png');

const getCachedCompanyLogoBase64 = (logoUrl?: string) => {
  if (typeof window === 'undefined') return undefined;
  try {
    const cachedBase64 = localStorage.getItem(COMPANY_LOGO_BASE64_KEY);
    const cachedUrl = localStorage.getItem(COMPANY_LOGO_URL_KEY);
    if (!cachedBase64) return undefined;
    if (logoUrl && cachedUrl && cachedUrl !== logoUrl) return undefined;
    return cachedBase64;
  } catch (e) {
    console.warn('getCachedCompanyLogoBase64 failed', e);
    return undefined;
  }
};

const storeCompanyLogoBase64 = (logoUrl: string, base64: string) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(COMPANY_LOGO_BASE64_KEY, base64);
    localStorage.setItem(COMPANY_LOGO_URL_KEY, logoUrl);
  } catch (e) {
    console.warn('storeCompanyLogoBase64 failed', e);
  }
};

const fetchLogoAsBase64 = async (logoUrl: string): Promise<string | undefined> => {
  if (!logoUrl) return undefined;
  try {
    const response = await fetch(logoUrl, { mode: 'cors', cache: 'no-store' });
    if (!response.ok) return undefined;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('fetchLogoAsBase64 failed', e);
    return undefined;
  }
};

const buildLabelPrintConfig = (
  size: { widthIn: number; heightIn: number },
  mode: LabelPrintMode
) => ({
  margins: { left: 0, top: 0, right: 0, bottom: 0 },
  units: 'in',
  size: { width: size.widthIn, height: size.heightIn },
  density: undefined,
  scaleContent: false,
  rasterize: mode === 'html',
  interpolation: 'nearest-neighbor',
  colorType: 'grayscale',
  jobName: 'Washday - Label'
});

const extractStyleTags = (html: string) =>
  (html.match(/<style[\s\S]*?<\/style>/gi) || []).join('');

const extractBodyContent = (html: string) => {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match ? match[1] : html;
};

type SimpleLabelLayout = {
  isCompactLabel: boolean;
  isMediumLabel: boolean;
  labelPadding: number;
  labelGap: number;
  logoMaxHeight: number;
  folioFontSize: number;
  infoFontSize: number;
  barcodeWidth: number;
  barcodeHeight: number;
  qrSize: number;
};

type CodeSizeConfig = {
  barcodeWidthMax: number;
  barcodeWidthMin: number;
  barcodeWidthPadding: number;
  barcodeHeightMax: number;
  barcodeHeightMin: number;
  barcodeHeightRatio: number;
  qrPadding: number;
  qrHeightRatio: number;
  qrMax: number;
  qrMin: number;
};

const CODE_SIZE_CONFIG: Record<LabelCodeSize, CodeSizeConfig> = {
  xs: {
    barcodeWidthMax: 150,
    barcodeWidthMin: 70,
    barcodeWidthPadding: 24,
    barcodeHeightMax: 70,
    barcodeHeightMin: 40,
    barcodeHeightRatio: 0.3,
    qrPadding: 22,
    qrHeightRatio: 0.3,
    qrMax: 96,
    qrMin: 60
  },
  s: {
    barcodeWidthMax: 230,
    barcodeWidthMin: 100,
    barcodeWidthPadding: 22,
    barcodeHeightMax: 110,
    barcodeHeightMin: 60,
    barcodeHeightRatio: 0.35,
    qrPadding: 18,
    qrHeightRatio: 0.38,
    qrMax: 160,
    qrMin: 100
  },
  m: {
    barcodeWidthMax: 300,
    barcodeWidthMin: 130,
    barcodeWidthPadding: 18,
    barcodeHeightMax: 150,
    barcodeHeightMin: 85,
    barcodeHeightRatio: 0.46,
    qrPadding: 14,
    qrHeightRatio: 0.48,
    qrMax: 210,
    qrMin: 130
  },
  l: {
    barcodeWidthMax: 340,
    barcodeWidthMin: 150,
    barcodeWidthPadding: 14,
    barcodeHeightMax: 180,
    barcodeHeightMin: 100,
    barcodeHeightRatio: 0.52,
    qrPadding: 12,
    qrHeightRatio: 0.54,
    qrMax: 250,
    qrMin: 150
  }
};

const resolveLabelCodeSize = (codeSize?: string): LabelCodeSize => {
  switch (codeSize) {
    case 'xs':
    case 'compact':
    case 'xsmall':
    case 'extra-small':
      return 'xs';
    case 's':
    case 'small':
      return 's';
    case 'm':
    case 'medium':
      return 'm';
    case 'l':
    case 'large':
      return 'l';
    default:
      return 's';
  }
};

const getCodeDimensions = (
  preset: { widthPx: number; heightPx: number },
  codeSize: LabelCodeSize
) => {
  const config = CODE_SIZE_CONFIG[codeSize];
  const barcodeWidth = Math.min(
    config.barcodeWidthMax,
    Math.max(config.barcodeWidthMin, preset.widthPx - config.barcodeWidthPadding)
  );
  const barcodeHeight = Math.min(
    config.barcodeHeightMax,
    Math.max(config.barcodeHeightMin, Math.round(preset.heightPx * config.barcodeHeightRatio))
  );
  const qrMaxByWidth = preset.widthPx - config.qrPadding;
  const qrMaxByHeight = Math.round(preset.heightPx * config.qrHeightRatio);
  const qrMax = Math.min(qrMaxByWidth, qrMaxByHeight);
  const qrSize = Math.min(config.qrMax, Math.max(config.qrMin, qrMax));
  return { barcodeWidth, barcodeHeight, qrSize };
};

const getSimpleLabelLayout = (
  labelSize: LabelSize,
  preset: { widthIn: number; heightIn: number; widthPx: number; heightPx: number },
  codeSize?: LabelCodeSize
): SimpleLabelLayout => {
  const isCompactLabel = labelSize === '2x1' || preset.widthIn <= 2 || preset.heightIn <= 1.5;
  const isMediumLabel =
    !isCompactLabel && (labelSize === '4x3' || labelSize === '3x3');

  const labelPadding = isCompactLabel ? 6 : isMediumLabel ? 10 : 12;
  const labelGap = isCompactLabel ? 4 : isMediumLabel ? 6 : 8;
  const logoMaxHeight = isCompactLabel ? 40 : isMediumLabel ? 70 : 120;
  const folioFontSize = isCompactLabel ? 14 : isMediumLabel ? 18 : 24;
  const infoFontSize = isCompactLabel ? 10 : isMediumLabel ? 12 : 14;

  const resolvedCodeSize = resolveLabelCodeSize(codeSize);
  const { barcodeWidth, barcodeHeight, qrSize } = getCodeDimensions(preset, resolvedCodeSize);

  return {
    isCompactLabel,
    isMediumLabel,
    labelPadding,
    labelGap,
    logoMaxHeight,
    folioFontSize,
    infoFontSize,
    barcodeWidth,
    barcodeHeight,
    qrSize
  };
};


export const buildSimpleLabelTicket = async (
  storeSettings,
  order,
  labelSize: LabelSize,
  companylogoURL = '',
  codeFormat?: 'qr' | 'barcode',
  options: WebLabelRenderOptions = {}
) => {
  const preset = getLabelPreset(labelSize);
  const settings = resolveLabelSettings(storeSettings?.labelTicketStructure);
  const t = options.t || translate;
  const text = (key: string) => escapeLabelHtml(t(`Labels:${key}`));
  const field = (key: string, value: unknown, className = '') =>
    `<div class="simple-label-field ${className}"><span class="simple-label-label">${text(key)}:</span> <span>${escapeLabelHtml(value)}</span></div>`;
  const resolvedCodeFormat = codeFormat || settings.orderCodeFormat;
  let logoBase64 = '';
  // A supplied company logo must never override the explicit logo opt-in.
  if (settings.printLogoOnTicket) {
    const companylogo = companylogoURL || (await getCompanyLogo()) || '';
    if (companylogo) {
      logoBase64 = getCachedCompanyLogoBase64(companylogo) ||
        (await fetchLogoAsBase64(companylogo)) || '';
      if (logoBase64) storeCompanyLogoBase64(companylogo, logoBase64);
    }
  }

  const {
    isCompactLabel, barcodeWidth, barcodeHeight, qrSize,
    labelPadding, labelGap, logoMaxHeight, infoFontSize
  } = getSimpleLabelLayout(labelSize, preset, resolveLabelCodeSize(settings.codeSize));
  const orderFolio = buildOrderFolio(order?.folioPrefix, order?.sequence) || '';
  const customerName = getCustomerFullName(
    order?.customer?.name || order?.customerInfo?.name,
    order?.customer?.lastName || order?.customerInfo?.lastName
  ) || '';
  const receptionDate = order?.createdDate ? moment(order.createdDate).format('DD/MM/YYYY') : '';
  const deliveryDate = settings.showDeliveryDate && order?.deliveryInfo?.date
    ? moment(order.deliveryInfo.date).format('DD/MM/YYYY') : '';
  const deliveryTime = settings.showDeliveryTime && order?.deliveryInfo?.fromTime
    ? formatTicketScheduleTime(order.deliveryInfo.fromTime) : '';
  const deliveryDateTime = [deliveryDate, deliveryTime].filter(Boolean).join(' ');

  const serviceFields: string[] = [];
  if (options.mode === 'service' && options.line) {
    if (settings.showServiceName) serviceFields.push(field('service', options.line.name, 'simple-label-service'));
    if (settings.showAssignedEmployee) {
      const employeeName = options.line.employeeState === 'unassigned'
        ? t('Labels:unassigned') : options.line.employeeState === 'unavailable'
          ? t('Labels:unavailable') : options.line.employeeName;
      serviceFields.push(field('employee', employeeName, 'simple-label-employee'));
    }
    if (settings.showServiceQuantity) serviceFields.push(field('quantity', options.line.quantity));
  }
  const fields: string[] = [];
  if (settings.showStoreName && storeSettings?.name) fields.push(field('store', storeSettings.name));
  if (settings.showCustomerName && customerName) fields.push(field('customer', customerName));
  if (settings.showEmisionDate && receptionDate) fields.push(field('reception', receptionDate));
  if (deliveryDateTime) fields.push(field('delivery', deliveryDateTime));
  if (settings.showTotalPieces) {
    const pieces = formatPiecesBreakdown(getPiecesBreakdown(order?.products || [], order?.buyAndGetProducts || []), {
      measurementUnits: storeSettings?.measurementUnits
    });
    fields.push(field('pieces', pieces));
  }
  if (settings.showPendingBalance) {
    const balance = getOrderAmountDue({ total: order?.total, paymentLines: order?.paymentLines });
    fields.push(field('pendingBalance', formatMoneyString(balance.toFixed(2), undefined, undefined, undefined, storeSettings?.currencySymbol || '$')));
  }

  // Reserve room for the folio and text before allocating a code. Small paper
  // uses a side-by-side layout; larger paper preserves the familiar vertical label.
  const compact = isCompactLabel;
  const codeBudget = compact ? preset.heightPx - 12 : Math.max(40,
    preset.heightPx - labelPadding * 2 - settings.orderFolioFontSizePx * 1.15 -
    (fields.length + serviceFields.length) * (infoFontSize * 1.2 + 3) -
    (logoBase64 ? Math.min(logoMaxHeight, 48) : 0) - labelGap * 3);
  const codeScale = Math.min(1, codeBudget / (resolvedCodeFormat === 'barcode' ? barcodeHeight : qrSize));
  const codeWidth = resolvedCodeFormat === 'barcode' ? barcodeWidth : Math.floor(qrSize * codeScale);
  const codeHeight = Math.floor((resolvedCodeFormat === 'barcode' ? barcodeHeight : qrSize) * codeScale);
  let codeImageUrl = '';
  if (settings.showOrderCodeInTicket && order?._id) {
    codeImageUrl = await getOrderCodeImageBase64({
      code: buildQrPayload({
        orderId: order._id,
        customerId: order?.customer?._id || order?.customer?.id
      }),
      format: resolvedCodeFormat,
      options: resolvedCodeFormat === 'barcode'
        ? { width: barcodeWidth, height: codeHeight, margin: 1 }
        : { width: codeWidth, margin: 1 }
    });
    // An empty code is not a successful render when scanning was requested.
    if (!codeImageUrl) throw new Error('Label code could not be generated');
  }

  const html = `
    <div id="label-render-root" style="width:${preset.widthPx}px;height:${preset.heightPx}px;overflow:hidden;">
      <div class="simple-label-root${compact ? ' simple-label--compact' : ''}" data-label-content
        style="--label-padding:${compact ? 3 : labelPadding}px;--label-gap:${compact ? 2 : labelGap}px;--logo-max-height:${compact ? 18 : Math.min(logoMaxHeight, 48)}px;--folio-font:${settings.orderFolioFontSizePx}px;--info-font:${compact ? 8 : infoFontSize}px;">
        ${logoBase64 ? `<img src="${escapeLabelHtml(logoBase64)}" alt="" class="simple-label-logo" />` : ''}
        ${orderFolio ? `<div class="simple-label-folio">${escapeLabelHtml(orderFolio)}</div>` : ''}
        <div class="simple-label-body">
          ${codeImageUrl ? `<img src="${escapeLabelHtml(codeImageUrl)}" alt="${text(resolvedCodeFormat === 'barcode' ? 'codeBarcode' : 'codeQr')}" class="simple-label-code" style="width:${codeWidth}px;height:${codeHeight}px;" />` : ''}
          <div class="simple-label-info">${serviceFields.join('')}${fields.join('')}</div>
        </div>
      </div>
    </div>
    <style>
      .simple-label-root { width:100%; box-sizing:border-box; padding:var(--label-padding); display:flex; flex-direction:column; align-items:center; gap:var(--label-gap); font-family:Arial,sans-serif; color:#000; background:#fff; text-align:center; overflow-wrap:anywhere; }
      .simple-label-root * { box-sizing:border-box; min-width:0; }
      .simple-label-logo { max-width:100%; max-height:var(--logo-max-height); object-fit:contain; }
      .simple-label-folio { font-size:var(--folio-font); font-weight:700; line-height:1.1; width:100%; }
      .simple-label-body { width:100%; display:flex; flex-direction:column; align-items:center; gap:var(--label-gap); }
      .simple-label-code { max-width:100%; object-fit:contain; flex-shrink:0; }
      .simple-label-info { width:100%; font-size:var(--info-font); line-height:1.2; text-align:left; display:grid; gap:3px; }
      .simple-label-label, .simple-label-service { font-weight:700; }
      .simple-label--compact .simple-label-body { flex-direction:row; align-items:flex-start; }
      .simple-label--compact .simple-label-info { gap:1px; }
      .simple-label--compact .simple-label-folio { font-size:min(var(--folio-font),20px); }
      .simple-label--compact .simple-label-code { max-width:42%; height:auto!important; }
    </style>`;

  return { html, widthIn:preset.widthIn, heightIn:preset.heightIn, widthPx:preset.widthPx, heightPx:preset.heightPx };
};

export async function downloadTicketAsPNG(order, storeSettings, t, ticketStructureConfig = 'ticketStructure', companylogoURL = '') {
  const ticketHTMLString = await generateTicketHTMLString(
    storeSettings,
    order,
    companylogoURL,
    t,
    false,
    false,
    storeSettings?.[ticketStructureConfig]
  );

  // Create a temporary container
  const tempContainer = document.createElement('div');

  tempContainer.style.width = 'auto';
  tempContainer.id = 'temp-ticket-node';
  tempContainer.style.display = 'flex';
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-999999'; // Keep it off-screen
  tempContainer.style.top = '0';
  tempContainer.style.height = 'auto'; // Allow content to size dynamically
  tempContainer.style.padding = '15px'; // Allow content to size dynamically
  tempContainer.style.zIndex = '-9999999'; // Keep it out of view
  tempContainer.style.overflow = 'visible'; // Ensure no scrollbars are added
  tempContainer.style.backgroundColor = 'white'; // ticket background color

  tempContainer.innerHTML = ticketHTMLString;

  document.body.appendChild(tempContainer);

  try {
    // Wait for all images to load
    const images = tempContainer.querySelectorAll('img');
    const imageLoadPromises = Array.from(images).map((img) => {
      return new Promise((resolve) => {
        if (img.complete) {
          resolve(true); // Image already loaded
        } else {
          img.onload = resolve;
          img.onerror = resolve; // Resolve even if the image fails to load
        }
      });
    });

    await Promise.all(imageLoadPromises);
    const dataUrl = await toPng(tempContainer);
    download(dataUrl, `${buildOrderFolio(order?.folioPrefix, order?.sequence)}_ticket.png`);
    return true;
  } catch (error) {
    console.error('Failed to generate PNG:', error);
    throw error;
  } finally {
    // Remove the temporary container
    document.body.removeChild(tempContainer);
  }
}

export async function getOrderCodeImageBase64({
  code,
  format = 'qr',
  options = {
    width: 150,
    margin: 1
  }
}: {
  code: string;
  format?: 'qr' | 'barcode';
  options?: {
    width?: number;
    margin?: number;
    height?: number;
  };
}): Promise<string> {
  if (!code) return '';

  if (format === 'barcode') {
    try {
      const jsBarcodeFn = (JsBarcode as any)?.default || JsBarcode;
      if (typeof jsBarcodeFn !== 'function') {
        throw new Error('JsBarcode is not a function');
      }
      const canvas = document.createElement('canvas');
      const margin = options?.margin ?? 6;
      const height = options?.height ?? 60;
      const widthOption = options?.width;
      const barWidth =
        typeof widthOption === 'number' && widthOption > 0 && widthOption <= 10
          ? widthOption
          : 1;
      let isValid = true;

      jsBarcodeFn(canvas, code, {
        format: 'CODE128',
        displayValue: false,
        margin,
        height,
        width: barWidth,
        lineColor: '#000',
        background: '#fff',
        valid: (valid: boolean) => {
          isValid = valid;
        }
      });
      if (!isValid) {
        console.warn('getOrderCodeImageBase64 invalid barcode payload', {
          codePreview: code?.slice(0, 80),
          length: code?.length,
          options
        });
        throw new Error('Invalid barcode payload');
      }

      const sourceWidth = canvas.width || 0;
      const sourceHeight = canvas.height || 0;
      if (sourceWidth <= 0 || sourceHeight <= 0) {
        console.warn('getOrderCodeImageBase64 barcode canvas empty', {
          sourceWidth,
          sourceHeight,
          codePreview: code?.slice(0, 80),
          length: code?.length,
          options
        });
        return '';
      }

      return canvas.toDataURL('image/png');
    } catch (e) {
      console.warn('getOrderCodeImageBase64 barcode failed', {
        error: e,
        codePreview: code?.slice(0, 80),
        length: code?.length,
        options
      });
      return '';
    }
  }

  return await QRCode.toDataURL(code, options);
}

const styleBlock = `
<style>
  @media print {
    .hideOnPrint {
      display: none;
    }
  }

  .banner-warning {
    background-color: #f8d7da;
    color: #721c24;
    padding: 10px;
    border-radius: 5px;
  }

  :root {
  --ticket-max-width: 80mm; /* Valor por defecto (80mm aprox) */
  }

  body, .ticket-container {
    margin: 0;
    padding: 0;
    font-family: 'Arial', sans-serif; /* o la que prefieras */
    font-size: 12px;
    line-height: 1.0;
  }

  .ticket-container {
    width: 100%;
    max-width: var(--ticket-max-width);
    margin: 0 auto;

  }

  .ticket-logo {
    width: 100%;
    max-height: 100px;
    object-fit: contain;
  }

  .ticket-header, .ticket-body, .ticket-footer {
    text-align: center;
    margin-bottom: 5px;
  }

  .ticket-separator {
    border: none;
    border-top: 1px solid #000;
    margin: 5px 0;
  }

  .text-left {
    text-align: left;
  }
  .text-right {
    text-align: right;
  }
  .text-center {
    text-align: center;
  }
  .bold {
    font-weight: bold;
  }

  /* Tabla de productos */
  .products-table {
    width: 100%;
    border-collapse: collapse;
  }

  .products-table td {
    padding: 3px 0;
    font-size: 12px;
  }

  .ticket-legend {
  white-space: pre-wrap;
  word-wrap: break-word;
}
  
  .btn-print {
    display: inline-block;
    padding: 5px 10px;
    font-size: 12px;
    margin: 10px 0;
    cursor: pointer;
  }
</style>
`;

// Estilos específicos para impresión en media carta (5.5in x 8.5in)
export const halfLetterStyleBlock = `
<style>
  @page {
    size: 5.5in 8.5in;
    margin: 0.2in;
  }

  :root {
    --ticket-max-width: 5.5in;
    --ticket-printable-width: 5.1in;
  }

  .ticket-container {
    width: 100%;
    max-width: var(--ticket-printable-width);
    min-width: auto;
    margin: 0 auto;
    padding: 0 0.1in;
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
  }

  body {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  /* Evitar cortes de página en bloques críticos */
  @media print {
    html, body {
      width: 100%;
      background: #ffffff;
    }

    body, .ticket-container {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .ticket-container {
      max-width: var(--ticket-printable-width);
    }

    .ticket-container,
    .ticket-header,
    .ticket-body,
    .ticket-footer,
    .products-table,
    .products-table tr,
    .ticket-legend,
    .ticket-separator,
    .bold,
    .text-right,
    .text-left,
    .text-center {
      page-break-inside: avoid;
    }
  }
</style>
`;

const getOrderFolioStyle = (orderFolioFontSizePx: number = 25): string => {
  return `<style>
  .ticket-order-folio {
    background-color: #000000;
    color: #ffffff;
    font-size: ${orderFolioFontSizePx}px;
    text-align: center;
    font-weight: bold;
    padding: 10px;
    border-radius: 5px;
    margin: 10px 0;
  }
  </style>`;
}
const getDynamicStyleBlock = ({ isQZPrinting, ticketLineMarginBlockPx }: { isQZPrinting: boolean, ticketLineMarginBlockPx: number }): string => {
  return isQZPrinting
    ? `<style>
      :root {
        --base-font-size: 14px; /* Para QZ printing en 80mm, por ejemplo */
        --ticket-line-margin-block-px: ${ticketLineMarginBlockPx}px;
      }
      body, .ticket-container {
        font-size: var(--base-font-size);
      }
      .ticket-container p{
        margin-block: var(--ticket-line-margin-block-px);
      }
      .ticket-separator{
        margin-block: var(--ticket-line-margin-block-px);
      }
    </style>`
    : `<style>
      :root {
        --base-font-size: 12px; /* Valor por defecto para otro caso */
        --ticket-line-margin-block-px: ${ticketLineMarginBlockPx}px;
      }
      body, .ticket-container {
        font-size: var(--base-font-size);
      }
      .ticket-container p{
        margin-block: var(--ticket-line-margin-block-px);
      }
      .ticket-separator{
        margin-block: var(--ticket-line-margin-block-px);
      }
    </style>`;
}

/**
 * Formats product attributes array into a single string.
 * For each attribute object, values (excluding _id) are joined with " / ".
 * Multiple attribute objects are joined with " ; ".
 */
function formatProductAttributesString(
  attributes: Array<Record<string, unknown>> | undefined
): string {
  if (!attributes?.length) return '';
  const blocks = attributes.map((attr) => {
    const values = Object.entries(attr)
      .filter(([key]) => key !== '_id')
      .map(([, value]) => (value != null && String(value).trim() !== '' ? String(value).trim() : null))
      .filter((v): v is string => v !== null);
    return values.join(' / ');
  });
  return blocks.filter((b) => b.length > 0).join('<br>');
}

const getProductLineHTML = ({
  order,
  prod,
  isExpress = false,
  currencySymbol = '$',
  showProductAttributes = false,
  measurementUnits,
  minimumChargeLabel = 'Cobro mínimo',
  quantityFieldLabel = 'Cantidad',
  financialDataMode = 'full',
}: {
  order: {
    orderType?: string | null,
    returnItemsChargeMode?: string | null,
  },
  prod: {
    name: string,
    quantity: number,
    price: number,
    extraAmount: number,
    discountAmount: number,
    discountAmountMode?: 'per_unit' | 'line_total',
    expressPrice: number,
    type?: string,
    minimumBillableQuantity?: number | null,
    isBuyAndGetProduct?: boolean,
    isFreeItem?: boolean,
    returnSourceOrderLineId?: string | null,
    attributes?: Array<Record<string, unknown>>
  },
  isExpress: boolean,
  currencySymbol?: string,
  showProductAttributes?: boolean,
  measurementUnits?: { weight: 'kg' | 'lb'; length: 'm' | 'ft' }
  minimumChargeLabel?: string,
  quantityFieldLabel?: string,
  financialDataMode?: 'full' | 'hidden',
}) => {
  const isMeasuredProduct =
    prod?.type === ProductType.LENGTH || prod?.type === ProductType.WEIGHT;
  const measuredUnitLabel = getMeasuredUnitLabel(prod?.type, measurementUnits);
  const measuredQuantityLabel = isMeasuredProduct
    ? `${new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(Number(prod.quantity || 0))} ${measuredUnitLabel}`
    : '';
  const displayName = isMeasuredProduct
    ? `${prod.name} (${measuredQuantityLabel})`
    : prod.name;
  const attributesText =
    showProductAttributes && prod.attributes?.length
      ? formatProductAttributesString(prod.attributes)
      : '';
  const attributesRow =
    attributesText.length > 0
      ? `
               <tr>
                 <td colspan="2" class="text-left">${attributesText}</td>
               </tr>`
      : '';

  if (financialDataMode === 'hidden') {
    const quantityLabel = isMeasuredProduct
      ? measuredQuantityLabel
      : String(Number(prod.quantity || 0));

    return `
               <tr>
                 <td colspan="2" class="bold text-left">${displayName}</td>
               </tr>
               ${attributesRow}
               <tr>
                 <td colspan="2" class="text-left">${quantityFieldLabel}: ${quantityLabel}</td>
               </tr>
             `;
  }

  // Calculamos precios como en tu código actual
  const charge = resolveEffectiveOrderLineCharge(order, prod, isExpress);
  const actualQuantity = charge.actualQuantity;
  const billableQuantity = charge.billableQuantity;
  const totalProductLine = charge.total;
  const minimumApplied = charge.total > 0 && billableQuantity > actualQuantity;
  const pricingQuantity = minimumApplied ? billableQuantity : actualQuantity;
  const displayQuantityForUnitPrice = isMeasuredProduct
    ? `${new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(pricingQuantity)} ${measuredUnitLabel}`
    : String(pricingQuantity);
  const unitProductPrice = minimumApplied
    ? charge.billableUnitPrice
    : charge.effectiveUnitPrice;
  const unitPriceLabel = `${formatMoneyString(
    unitProductPrice.toFixed(2),
    undefined,
    undefined,
    undefined,
    currencySymbol
  )}${isMeasuredProduct ? `/${measuredUnitLabel}` : ''}`;

  // Estructura: nombre, opcionalmente atributos, luego cantidad x precio / total
  const minimumChargeRow = minimumApplied
    ? `
               <tr>
                 <td colspan="2" class="text-left">${minimumChargeLabel}: ${new Intl.NumberFormat('es-MX', {
                   minimumFractionDigits: 0,
                   maximumFractionDigits: 3
                 }).format(Number(prod.minimumBillableQuantity || 0))} ${measuredUnitLabel}</td>
               </tr>`
    : '';

  return `
               <tr>
                 <td colspan="2" class="bold text-left">${displayName}</td>
               </tr>
               ${attributesRow}
               ${minimumChargeRow}
               <tr>
                 <td class="text-left">${displayQuantityForUnitPrice} x ${unitPriceLabel}</td>
                 <td class="text-right">${formatMoneyString(totalProductLine.toFixed(2), undefined, undefined, undefined, currencySymbol)}</td>
               </tr>
             `;
}

export async function generateTicketHTMLString(
  storeSettings = {
    currencySymbol: '$',
    printingConfig: {
      // @deprecated, TODO: REMOVE ONCE STOP USING IT COMPLETELY
      printLogoOnTicket: false 
    },
    loyaltySettings: { enabled: false },
    name: 'Lavanderia',
    phone: '',
    measurementUnits: { weight: 'kg' as const, length: 'm' as const }
  },
  finalOrderObj,
  logoUrl = DEFAULT_TICKET_LOGO_URL,
  t = (str: string) => str,
  isQZPrinting = true,
  showPrintButton = false,
  ticketStructure: ITicketStructure | ITicketForLaundryStructure = {
    showSubtotal: true,
    showEmisionDate: true,
    showEmisionTime: true,
    showLegend: true,
    showPaymentMethod: true, //@Deprecated, we will remove this from the ticket
    showStorePhone: true,
    showCustomerPoints: false,
    showDeliveryDate: true,
    showDeliveryTime: true,
    showPickupInfo: false,
    showServiceType: false,
    showIssuerTaxInfo: false,
    whatsappPhone: '',
    tax_id: '',
    tax_id_label: '',
    ticketLegendText: '¡GRACIAS POR SU COMPRA!',
    showStoreName: true,
    showCustomerName: false,
    showCustomerPhone: false,
    showCreatedByName: true,
    showOrderNotes: false,
    showTotalPieces: false,
    showTaxes: false,
    showDiscounts: true,
    showCredit: true,
    showStoreAddress: false,
    showPaymentLines: false,
    groupProductsBySection: false,
    showOrderCodeInTicket: false,
    orderCodeFormat: '',
    showCustomerPrivateNotes: false,
    orderFolioFontSizePx: 25,
    showOrderFolioAsNormalTicketLine: false,
    customerNameFontSize: 'medium' as 'small' | 'medium' | 'large',
    ticketLineMarginBlockPx: 12,
    showProductAttributes: false,
    printLogoOnTicket: false
  },
  companyTaxInfo?: { name?: string; legal_name?: string; tax_system?: string }
) {
  let codeImageUrl = '';
  const currencySymbol = storeSettings?.currencySymbol || '$';
  const financialDataMode = resolveFinancialDataMode({ ticketStructure });
  const showFinancialData = financialDataMode === 'full';
  const printLogoOnTicketResolved = ticketStructure?.printLogoOnTicket ?? false;
  if (ticketStructure.showOrderCodeInTicket) {
    const qrPayload = buildQrPayload({
      orderId: finalOrderObj?._id,
      customerId: (finalOrderObj?.customer as any)?._id || (finalOrderObj?.customer as any)?.id
    });
    codeImageUrl = await getOrderCodeImageBase64({
      code: qrPayload,
      format: 'qr',
      options: {
        width: 150,
        margin: 1
      }
    });
  }

  const customerNameFontSize = getFontSize(ticketStructure.customerNameFontSize);
  const ticketLineMarginBlockPx = ticketStructure.ticketLineMarginBlockPx || 12;
  // Calcula totales, productos, etc. como lo hacías antes
  const paymentLinesTotal = finalOrderObj?.paymentLines?.reduce((prev, next) => {
    return prev + (next?.amountPaid || 0);
  }, 0) || 0;
  const amountDue = getOrderAmountDue({ total: finalOrderObj?.total, paymentLines: finalOrderObj?.paymentLines });

  const orderProducts = finalOrderObj?.products || [];
  const orderBuyAndGerProducts = finalOrderObj?.buyAndGetProducts || [];
  const orderAllProducts = [
    ...orderProducts,
    ...orderBuyAndGerProducts.map((product) => ({
      ...product,
      isBuyAndGetProduct: true
    }))
  ];
  const legendText = (ticketStructure.ticketLegendText || '').replace(/\n/g, '<br>');

  const isExpressService = (finalOrderObj?.express);
  const serviceLabel = isExpressService ? 'Express' : 'Regular';
  const pickupInfoLabel = buildPickupInfoLabel(finalOrderObj?.pickupInfo);

  const paymentLinesMap = finalOrderObj?.paymentLines?.map((paymentLine) => {
    return {
      paymentMethod: t(`FieldName:paymentMethods.${paymentLine?.paymentMethod}`),
      amountPaid: formatMoneyString(paymentLine?.amountPaid || 0, undefined, undefined, undefined, currencySymbol)
    }
  });

  // WE GROUP THE PRODUCTS BY SECTION
  const groupedProductsBySection = orderAllProducts.reduce((acc, curr: {
    section: string | { _id: string, name: string },
    name: string,
    quantity: number,
    price: number,
    extraAmount: number,
    discountAmount: number,
    [key: string]: any
  }) => {
    const sectionId = typeof curr.section === 'string' || !curr.section ? (curr.section || "") : curr.section._id;
    if (!acc[sectionId]) {
      acc[sectionId] = {
        _id: sectionId,
        name: typeof curr.section === 'string' || !curr.section ? (curr.section || "") : curr.section.name,
        products: []
      };
    }
    acc[sectionId].products.push(curr);
    return acc;
  }, {});

  const productsBySectionHTML = Object.values(groupedProductsBySection).map((section: any) => {
    const sectionHTML = `
              <tr>
                <td colspan="2" class="bold text-left">${section.name}</td>
              </tr>
    `;
    const productsHTML = section.products.map((prod: any) => {
      return getProductLineHTML({
        order: finalOrderObj,
        prod,
        isExpress: finalOrderObj?.express,
        currencySymbol,
        showProductAttributes: ticketStructure.showProductAttributes,
        measurementUnits: storeSettings?.measurementUnits,
        minimumChargeLabel: t('NewOrder:minimumChargeLabel'),
        quantityFieldLabel: t('FieldName:quantity'),
        financialDataMode,
      });
    }).join('');
    return `${sectionHTML}${productsHTML}`;
  }).join('');

  const nonGroupedProductsHTML = orderAllProducts
    .map((prod) => {
      return getProductLineHTML({
        order: finalOrderObj,
        prod,
        isExpress: finalOrderObj?.express,
        currencySymbol,
        showProductAttributes: ticketStructure.showProductAttributes,
        measurementUnits: storeSettings?.measurementUnits,
        minimumChargeLabel: t('NewOrder:minimumChargeLabel'),
        quantityFieldLabel: t('FieldName:quantity'),
        financialDataMode,
      });
    })
    .join('');

  // legal_name = nombre fiscal o razon social de la organizacion
  // tax_system = regimen fiscal de la organizacion
  const hasIssuerTaxInfo =
    companyTaxInfo?.legal_name ||
    companyTaxInfo?.tax_system ||
    ticketStructure?.tax_id;
  const taxIdLabel = ticketStructure?.tax_id_label || 'RFC';

  const displayTotals = resolveOrderDisplayTotals(finalOrderObj);
  const subtotalForDisplay = displayTotals.subtotal;
  const discountForDisplay = displayTotals.discount;
  const taxesLabel = displayTotals.taxesLabel === 'taxesIncluded'
    ? 'Impuestos incluidos'
    : 'Impuestos';

  // Aquí generamos el HTML
  const html = `
  <!DOCTYPE html>
  <html>
    <head>
      ${styleBlock}
      ${getDynamicStyleBlock({ isQZPrinting, ticketLineMarginBlockPx })}
      ${getOrderFolioStyle(ticketStructure?.orderFolioFontSizePx)}
      <script>
        async function waitForAssets() {
          const images = Array.from(document.images);
          await Promise.all(
            images.map(
              (img) =>
                img.complete
                  ? Promise.resolve()
                  : new Promise((resolve) => {
                      img.onload = resolve;
                      img.onerror = resolve;
                    })
            )
          );
          if (document.fonts && document.fonts.ready) {
            try { await document.fonts.ready; } catch (e) {}
          }
          await new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));
        }
        async function printInsider() {
          await waitForAssets();
          window.print();
          window.document.close();
        }
      </script>
    </head>
    <body>
      <div class="ticket-container">

      <!-- BOTÓN IMPRIMIR (opcional) -->
      ${showPrintButton
      ? `<div class="text-center hideOnPrint">
              <div class="banner-warning"><p class="bold">No se ha encontrado ninguna impresora disponible, por favor verifique su configuración</p></div>
              <button class="btn-print hideOnPrint" onclick="printInsider(); return false;">Imprimir</button>
            </div>`
      : ''
    }
        <!-- ENCABEZADO -->
        <div class="ticket-header">
          ${printLogoOnTicketResolved
      ? `<img src="${logoUrl}" alt="Logo" class="ticket-logo" />`
      : ''
    }
          ${ticketStructure.showStoreName
      ? `<p class="bold">${storeSettings?.name || ''}</p>`
      : ''
    }
          ${ticketStructure.showStoreAddress
      ? `<p>${wdUtils.getFormattedAddress({
        street: storeSettings?.streetAddress,
        zipCode: storeSettings?.zipCode,
        city: storeSettings?.city,
        state: storeSettings?.state,
        country: storeSettings?.country?.name,
      }) || ''}</p>`
      : ''
    }
    ${ticketStructure.showStorePhone
      ? `<p>Tel: ${storeSettings?.phone || ''}</p>`
      : ''
    }
    ${ticketStructure.whatsappPhone
      ? `<p>WhatsApp: ${ticketStructure.whatsappPhone}</p>`
      : ''
    }

    ${ticketStructure.showIssuerTaxInfo && hasIssuerTaxInfo
      ? `<div class="text-left">
          ${hasIssuerTaxInfo && companyTaxInfo?.legal_name ? `<p><span class="bold">Razón social:</span> ${companyTaxInfo.legal_name}</p>` : ''}
          ${hasIssuerTaxInfo && companyTaxInfo?.tax_system ? `<p><span class="bold">Régimen fiscal:</span> ${companyTaxInfo.tax_system} - ${RegimenFiscalEnum[companyTaxInfo.tax_system as RegimenFiscalType]}</p>` : ''}
          ${ticketStructure?.tax_id ? `<p><span class="bold">${taxIdLabel}:</span> ${ticketStructure.tax_id}</p>` : ''}
        </div>`
      : ''
    }
        </div>

        <hr class="ticket-separator"/>

        <!-- DATOS DE LA ORDEN -->
        <div class="ticket-body text-left">
        ${ticketStructure.showOrderFolioAsNormalTicketLine
      ? `<p><span class="bold">Pedido:</span> ${buildOrderFolio(finalOrderObj?.folioPrefix, finalOrderObj?.sequence) || ''}</p>`
      : `<p class="ticket-order-folio"> ${buildOrderFolio(finalOrderObj?.folioPrefix, finalOrderObj?.sequence) || ''}</p>`
    }
    ${ticketStructure.showCustomerName
      ? `<p style="font-size: ${customerNameFontSize}px; margin-bottom: 16px;"> ${finalOrderObj?.customer?.name || ''} ${finalOrderObj?.customer?.lastName || ''}</p>`
      : ''
    }
    ${ticketStructure.showCustomerPhone && finalOrderObj?.customer?.phone
      ? `<p><span class="bold">Tel Cliente:</span> ${finalOrderObj?.customer?.phone || ''}</p>`
      : ''
    }
    ${finalOrderObj?.customer?.frequentCustomerCard?.number?.trim()
      ? `<p style="overflow-wrap: anywhere;"><span class="bold">${escapeLabelHtml(t('FieldName:frequentCustomerCard.ticketLabel'))}</span> ${escapeLabelHtml(finalOrderObj.customer.frequentCustomerCard.number.trim())}</p>`
      : ''
    }
    ${showFinancialData && ticketStructure.showCustomerPoints && finalOrderObj?.loyaltyPointsExpectedToEarn > 0
      ? `<p><span class="bold">Puntos estimados:</span> ${finalOrderObj?.loyaltyPointsExpectedToEarn || 0}</p>`
      : ''
    }
    ${ticketStructure.showEmisionDate
      ? `<p><span class="bold">Fecha emisión:</span> ${moment(finalOrderObj?.createdDate).format('DD/MM/YYYY')}</p>`
      : ''
    }
          ${ticketStructure.showEmisionTime
      ? `<p><span class="bold">Hora emisión:</span> ${moment(finalOrderObj?.createdDate).format('hh:mm a')}</p>`
      : ''
    }
          ${ticketStructure.showCreatedByName
      ? `<p><span class="bold">Atendió:</span> ${finalOrderObj?.createdBy?.name || ''}</p>`
      : ''
    }
    ${ticketStructure.showServiceType
      ? `<p><span class="bold">Tipo de servicio:</span> ${serviceLabel}</p>`
      : ''
    }
          ${ticketStructure.showDeliveryDate
      ? `<p><span class="bold">Entrega:</span> ${moment(finalOrderObj?.deliveryInfo?.date).format('DD/MM/YYYY')} ${ticketStructure.showDeliveryTime
        ? formatTicketScheduleTime(finalOrderObj?.deliveryInfo?.fromTime)
        : ''
      }</p>`
      : ''
    }
    ${ticketStructure.showPickupInfo && pickupInfoLabel
      ? `<p><span class="bold">Recolección:</span> ${pickupInfoLabel}</p>`
      : ''
    }
          ${ticketStructure.showTotalPieces
      ? `<p><span class="bold">Piezas:</span> ${formatPiecesBreakdown(getPiecesBreakdown(orderProducts, orderBuyAndGerProducts), { measurementUnits: storeSettings?.measurementUnits })}</p>`
      : ''
    }
          ${ticketStructure.showOrderNotes
      ? `<p>${finalOrderObj?.notes || ''}</p>`
      : ''
    }
          ${ticketStructure.showCustomerPrivateNotes
      ? `<p>${finalOrderObj?.customer?.privateNotes || ''}</p>`
      : ''
    }
        </div>

        <hr class="ticket-separator"/>

        <!-- TABLA DE PRODUCTOS (2 filas por producto) -->
        <table class="products-table">
          <tbody>
            ${ticketStructure.groupProductsBySection
      ? productsBySectionHTML
      : nonGroupedProductsHTML
    }
          </tbody>
        </table>

        <hr class="ticket-separator"/>

        ${showFinancialData ? `<!-- TOTALES -->
        <div class="totals">
          ${ticketStructure.showSubtotal !== false
      ? `<p class="bold">
            <span>Subtotal:</span>
            <span>${formatMoneyString(subtotalForDisplay, undefined, undefined, undefined, currencySymbol)}</span>
          </p>`
      : ''
    }
          ${ticketStructure.showDiscounts
      ? `<p class="bold">
                  <span>Descuento:</span>
                  <span>${formatMoneyString(discountForDisplay, undefined, undefined, undefined, currencySymbol)}</span>
                </p>`
      : ''
    }
          ${ticketStructure.showCredit
      ? `<p class="bold">
                  <span>Crédito:</span>
                  <span>${formatMoneyString(finalOrderObj?.creditApplied || 0, undefined, undefined, undefined, currencySymbol)}</span>
                </p>`
      : ''
    }
    ${storeSettings?.loyaltySettings?.enabled && finalOrderObj?.loyaltyDiscountAmount > 0
      ? `<p class="bold">
                  <span>Descuento por puntos:</span>
                  <span>${formatMoneyString(finalOrderObj?.loyaltyDiscountAmount || 0, undefined, undefined, undefined, currencySymbol)}</span>
                </p>`
      : ''
    }
          ${ticketStructure.showTaxes
      ? `<p class="bold">
                  <span>${taxesLabel}:</span>
                  <span>${formatMoneyString(finalOrderObj?.taxesTotal || 0, undefined, undefined, undefined, currencySymbol)}</span>
                </p>`
      : ''
    }
          ${(finalOrderObj?.delivery || finalOrderObj?.pickup)
      ? `<p class="bold">
                  <span>Servicio a domicilio:</span>
                  <span>${formatMoneyString(finalOrderObj?.shippingServiceTotal || 0, undefined, undefined, undefined, currencySymbol)}</span>
                </p>`
      : ''
    }
          <p class="bold">
            <span>Total:</span>
            <span>${formatMoneyString(finalOrderObj?.total || 0, undefined, undefined, undefined, currencySymbol)}</span>
          </p>
          <p class="bold">
            <span>Pago:</span>
            <span>${paymentLinesTotal}</span>
          </p>
          <p class="bold">
            <span>Debe:</span>
            <span>${formatMoneyString(amountDue.toFixed(2), undefined, undefined, undefined, currencySymbol)}</span>
          </p>
          ${ticketStructure.showPaymentLines
      ? paymentLinesMap.map((paymentLine) => {
        return `<p class="bold">
                  <span>${paymentLine?.paymentMethod}:</span>
                  <span>${paymentLine?.amountPaid}</span>
                </p>`
      }).join('')
      : ''
    }
        </div>

        <hr class="ticket-separator"/>` : ''}
                        <!-- ORDER QR CODE -->
        ${ticketStructure.showOrderCodeInTicket && codeImageUrl
      ? `
      <div class="text-center" style="margin: 10px 0;">
        <img src="${codeImageUrl}" alt="Order Code" width="150" />
      </div>
      `
      : ''
    }

        <!-- LEYENDA FINAL -->
        ${ticketStructure.showLegend
      ? `<pre class="ticket-legend text-center">${legendText}</pre>`
      : ''
    }
      </div>
    </body>
  </html>
  `;

  return html;
}


const openTicketNewTab = async (
  order,
  storeSettings,
  companyLogo,
  t,
  showPrintButton = false,
  ticketStructureToPrint: ITicketStructure | ITicketForLaundryStructure,
  companyTaxInfo?: { name?: string; legal_name?: string; tax_system?: string }
) => {
  const win = window.open();
  const stringElementBrowserPrint = await generateTicketHTMLString(
    storeSettings,
    order,
    companyLogo,
    t,
    false,
    showPrintButton,
    ticketStructureToPrint,
    companyTaxInfo
  );
  win.document.write(stringElementBrowserPrint);
};

// Genera HTML para ticket en media carta reutilizando la plantilla existente
export const generateHalfLetterTicketHTMLString = async (
  storeSettings,
  finalOrderObj,
  logoUrl: string | undefined = undefined,
  t = (str: string) => str,
  ticketStructureConfig: 'ticketForLaundryStructure' | 'ticketStructure' = 'ticketStructure',
  companyTaxInfo?: { name?: string; legal_name?: string; tax_system?: string }
) => {
  // Resolver logo igual que el flujo QZ: si no se pasa, intentar obtener el de la compañía
  let resolvedLogo = logoUrl;
  const printLogoOnTicketResolved = storeSettings?.[ticketStructureConfig]?.printLogoOnTicket
  if (!resolvedLogo && printLogoOnTicketResolved) {
    try {
      resolvedLogo = await getCompanyLogo();
    } catch (e) {
      console.warn('No se pudo cargar logo para media carta', e);
    }
  }

  const baseHtml = await generateTicketHTMLString(
    storeSettings,
    finalOrderObj,
    resolvedLogo,
    t,
    false, // isQZPrinting
    true, // showPrintButton
    storeSettings?.[ticketStructureConfig],
    companyTaxInfo
  );

  // Inserta estilos de media carta antes de </head>
  if (baseHtml.includes('</head>')) {
    return baseHtml.replace('</head>', `${halfLetterStyleBlock}</head>`);
  }

  // Fallback en caso de no encontrar head
  return `${halfLetterStyleBlock}${baseHtml}`;
};

// Abre nueva pestaña con el ticket en media carta y botón de imprimir
export const openHalfLetterTicketNewTab = async (
  order,
  storeSettings,
  companyLogo,
  t,
  ticketStructureConfig: 'ticketForLaundryStructure' | 'ticketStructure' = 'ticketStructure',
  companyTaxInfo?: { name?: string; legal_name?: string; tax_system?: string }
) => {
  const win = window.open();
  const html = await generateHalfLetterTicketHTMLString(
    storeSettings,
    order,
    companyLogo,
    t,
    ticketStructureConfig,
    companyTaxInfo
  );
  win.document.write(html);
};

const getCompanyLogo = async () => {
  const token = cookies.get('token');
  try {
    const res = await axios.get(
      `${import.meta.env.REACT_APP_BACKEND_BASE_URL}${GET_COMPANY_LOGO}`,
      {
        headers: token
          ? {
            Authorization: `Bearer ${token}`
          }
          : undefined
      }
    );
    const logoUrl = res?.data?.data?.logo;
    if (!logoUrl) return undefined;

    const cached = getCachedCompanyLogoBase64(logoUrl);
    if (cached) {
      return cached;
    }

    const fetched = await fetchLogoAsBase64(logoUrl);
    if (fetched) {
      storeCompanyLogoBase64(logoUrl, fetched);
      return fetched;
    }

    return logoUrl;
  } catch (e) {
    console.warn('getCompanyLogo failed', e);
    return undefined;
  }
};

export const getHTMLTicket = async (
  order,
  storeSettings,
  t,
  ticketStructureConfig: 'ticketForLaundryStructure' | 'ticketStructure' = 'ticketStructure',
  companylogo = '',
  companyTaxInfo?: { name?: string; legal_name?: string; tax_system?: string }
) => {
  return await generateTicketHTMLString(
    storeSettings,
    order,
    companylogo,
    t,
    true,
    false,
    storeSettings?.[ticketStructureConfig],
    companyTaxInfo
  );;
}

export const tryPrintTicket = async (
  order,
  storeSettings,
  t,
  ticketStructureConfig: 'ticketForLaundryStructure' | 'ticketStructure' = 'ticketStructure',
  companylogoURL = '',
  printCopiesFromConfig = false,
  companyTaxInfo?: { name?: string; legal_name?: string; tax_system?: string }
) => {
  try {
    const ticketStructureToPrint = storeSettings?.[ticketStructureConfig];
    const printLogoOnTicketResolved = ticketStructureToPrint?.printLogoOnTicket;
    let companylogo = companylogoURL;
    if (!companylogoURL && printLogoOnTicketResolved) {
      companylogo = await getCompanyLogo();
    }

    // E2E mode: skip QZ checks and open a lightweight browser print tab without blocking UI flow.
    if (import.meta.env.VITE_E2E_DISABLE_QZ === '1') {
      if (order?._id) {
        window.open(`/orders/${order._id}/print/half-letter`, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    const id = showNotification({
      loading: true,
      title: 'Verificando conexion a impresora',
      type: 'Info'
    });
    const printer = new Printer();
    const connectResponseError = await printer.verifyQZConnection();
    const printerName = cookies.get('printer') || localStorage.getItem('printer');
    const copies = printCopiesFromConfig ? ticketStructureToPrint?.printQuantity : 1;

    // In case of a QZ Tray error, we try to print the ticket using window.print
    if (!connectResponseError.ok) {
      updateNotification({
        id,
        loading: false,
        title: connectResponseError.message || 'No pudimos conectar impresora, por favor verifique su configuracion y conexion a QZ Tray.'
      } as NotificationData);
      await openTicketNewTab(order, storeSettings, companylogo, t, true, ticketStructureToPrint, companyTaxInfo);
      return;
    }
    if (!printerName) {
      await openTicketNewTab(order, storeSettings, companylogo, t, true, ticketStructureToPrint, companyTaxInfo);
      return;
    }

    const stringElement = await generateTicketHTMLString(
      storeSettings,
      order,
      companylogo,
      t,
      true,
      false,
      ticketStructureToPrint,
      companyTaxInfo
    );


    //preventing NaN or negative numbers from being used as copies number and max number currently as 3
    const copiesNumberSanitized = isNaN(Number(copies)) ? 1 : Math.max(Number(copies), 1);
    const copiesNumberSanitizedMaximun = Math.min(copiesNumberSanitized, 3);

    const printerPaperSize =
      cookies.get('printerPaperSize') || localStorage.getItem('printerPaperSize');
    const ticketPaperWidth = resolveEscPosPaperWidth(printerPaperSize);
    const ticketPrintFormat = shouldUseEscPosTicketFormat(ticketStructureToPrint) ? 'escpos' : 'html';

    if (ticketPrintFormat === 'escpos') {
      try {
        const escPosBytes = await generateEscPosTicketForFrontend({
          order,
          storeSettings,
          ticketStructure: ticketStructureToPrint,
          companyTaxInfo,
          companylogo,
          t,
          paperWidth: ticketPaperWidth
        });
        const escPosPrintData = createEscPosQzPrintData(escPosBytes);

        for (let i = 0; i < copiesNumberSanitizedMaximun; i++) {
          await printer.print(
            printerName,
            {
              jobName: 'Washday - Ticket ESC/POS',
            },
            escPosPrintData,
          );
        }

        updateNotification({
          id,
          loading: false,
          title: 'Impresora encontrada...',
          message: printerName
        });
        return;
      } catch (escPosError) {
        // Keep the legacy HTML path as a safety net while stores migrate
        // structure-by-structure to persisted ESC/POS printing.
        console.warn('ESC/POS ticket print failed, falling back to HTML printing', escPosError);
      }
    }

    for (let i = 0; i < copiesNumberSanitizedMaximun; i++) {
      //DOING IT THIS WAY BECAUSE OF USING COPIES PARAM MIGHT NOT CUT THE PAPER AFTER PRINTING EACH COPY
      await printer.print(
        printerName,
        {
          margins: { left: 0.1, bottom: 0.1 },
          interpolation: 'bicubic',
          size: {
            width: ticketPaperWidth
          },
          units: 'mm',
          rasterize: true,
          // copies: copies // this may be only supported on QZ TRAY PIXEL printing mode
        },
        stringElement,
      );
    }

    updateNotification({
      id,
      loading: false,
      title: 'Impresora encontrada...',
      message: printerName
    });
  } catch (e) {
  }
};

export type LabelPrintBatchResult<T> = {
  /** Accepted by QZ; this does not acknowledge physical paper output. */
  sent: T[];
  failed: Array<{ item: T; error: unknown }>;
  /** Prepared in the browser fallback document; these have not been sent. */
  prepared: T[];
  fallback: boolean;
  busy: boolean;
};

const printLabelBatch = async <T,>(
  order: LabelOrderInput | null | undefined,
  storeSettings: LabelStoreInput,
  t: LabelTranslator,
  items: readonly T[],
  renderOptions: (item: T) => LabelRenderOptions,
  itemName: (item: T) => string,
  companylogoURL: string
): Promise<LabelPrintBatchResult<T>> => {
  const result: LabelPrintBatchResult<T> = { sent: [], failed: [], prepared: [], fallback: false, busy: false };
  const message = (key: string, values?: Record<string, unknown>) => String(t(`Labels:${key}`, values));
  if (isPrintingLabel) {
    showNotification({ type: 'Info', title: message('busy') });
    return { ...result, busy: true };
  }
  if (!items.length) {
    showNotification({ type: 'Info', title: message('emptySelection') });
    return result;
  }
  isPrintingLabel = true;
  let notificationId: string | undefined;
  try {
    notificationId = showNotification({ loading: true, title: message('preparing'), type: 'Info' });
    const printer = new Printer();
    const { labelPrinter, labelSize } = getLabelPrefs();
    let canUseQz = false;
    if (labelPrinter) {
      try {
        canUseQz = (await printer.verifyQZConnection(labelPrinter)).ok;
      } catch {
        // No job has been submitted, so browser fallback is still safe.
      }
    }
    const build = (item: T) => buildSimpleLabelTicket(
      storeSettings || {}, order, labelSize, companylogoURL, undefined, { ...renderOptions(item), t }
    );
    if (!canUseQz) {
      result.fallback = true;
      const pages: string[] = [];
      for (const item of items) {
        try {
          pages.push((await build(item)).html);
          result.prepared.push(item);
        } catch (error) {
          result.failed.push({ item, error });
        }
      }
      const { widthIn, heightIn } = getLabelPreset(labelSize);
      if (pages.length && !openLabelInNewTab(pages, { widthIn, heightIn })) {
        const error = new Error('Label fallback popup blocked');
        result.failed.push(...result.prepared.map(item => ({ item, error })));
        result.prepared = [];
        updateNotification({ id: notificationId, loading: false, color: 'red', title: message('popupBlocked'), message: message('popupBlocked') });
        return result;
      }
      updateNotification({
        id: notificationId, loading: false, color: result.failed.length ? 'red' : 'blue',
        title: message(result.failed.length ? 'failed' : 'fallbackReady'),
        message: result.failed.length
          ? message('batchPartial', { sent: 0, failed: result.failed.length, failedNames: result.failed.map(({ item }) => itemName(item)).join(', ') })
          : message('fallbackHint')
      });
      return result;
    }

    const batch = await sendLabelBatch(items, async (item) => {
      const { html, widthIn, heightIn, widthPx, heightPx } = await build(item);
      let pdfBase64: string | undefined;
      try {
        const base64Png = normalizeBase64Image(await renderHtmlToPngBase64({
          html, widthPx, heightPx, pixelRatio: 3, debugDownload: false
        }));
        if (!base64Png.startsWith('iVBORw0KGgo')) throw new Error('Invalid label PNG header');
        pdfBase64 = await pngBase64ToPdfBase64(base64Png, { widthIn, heightIn });
      } catch {
        // Only preparation can fall back. A rejected print may already have
        // reached QZ/the printer and must never trigger a second format/retry.
      }
      if (pdfBase64) {
        await printer.print(labelPrinter, buildLabelPrintConfig({ widthIn, heightIn }, 'pdf'), {
          type: 'pixel', format: 'pdf', flavor: 'base64', data: pdfBase64
        });
      } else {
        const fittedHtml = await fitLabelHtmlForPrint({ html, widthPx, heightPx });
        await printer.print(labelPrinter, buildLabelPrintConfig({ widthIn, heightIn }, 'html'), fittedHtml,
          { type: 'pixel', format: 'html', flavor: 'plain' });
      }
    });
    result.sent = batch.sent;
    result.failed = batch.failed;
    updateNotification({
      id: notificationId, loading: false, color: result.failed.length ? 'red' : 'green',
      title: message(result.failed.length ? 'failed' : 'sent'),
      message: result.failed.length
        ? message('batchPartial', { sent: result.sent.length, failed: result.failed.length, failedNames: result.failed.map(({ item }) => itemName(item)).join(', ') })
        : message('batchSent', { sent: result.sent.length, count: result.sent.length })
    });
    return result;
  } catch (error) {
    // Fail any remaining items explicitly and always stop the loading state.
    result.failed.push(...items.filter(item => !result.sent.includes(item) && !result.failed.some(failure => failure.item === item)).map(item => ({ item, error })));
    if (notificationId) updateNotification({ id: notificationId, loading: false, color: 'red', title: message('failed'), message: message('failed') });
    else showNotification({ type: 'Error', title: message('failed') });
    return result;
  } finally {
    isPrintingLabel = false;
  }
};

// Preserve all existing automatic callers: this always produces one order label.
export const tryPrintLabel = async (order: LabelOrderInput | null | undefined, storeSettings: LabelStoreInput, t: LabelTranslator = translate, _extraLines: string[] = [], companylogoURL = '') =>
  printLabelBatch(order, storeSettings, t, [order], () => ({ mode: 'order' }), () => String(t('Labels:orderLabel')), companylogoURL);

export const tryPrintServiceLabels = async (
  order: LabelOrderInput,
  storeSettings: LabelStoreInput,
  t: LabelTranslator,
  selectedLines: ServiceLabelLine[],
  companylogoURL = ''
): Promise<LabelPrintBatchResult<ServiceLabelLine>> => {
  const unique = selectedLines.filter((line, index) => selectedLines.findIndex(candidate => candidate.key === line.key) === index);
  return printLabelBatch(order, storeSettings, t, unique, line => ({ mode: 'service', line }), line => line.name, companylogoURL);
};
